import { ForbiddenError } from '../../errors/ForbiddenError';
import { ForbiddenErrors } from '../../errors/types';

import type {
  IServerTableConfigResponse,
  ParseBetLimitsType,
  ParsePayoutsType,
  PayoutType,
  TableConfigType,
} from './types';
import { WinMessageBranding } from './types';
import { getJurisdictionData } from './utils';

const chipColorCodesNormalizer = (colorCodes: string | string[] | undefined): string[] => {
  if (typeof colorCodes === 'string') {
    return colorCodes.split(',');
  }

  return Array.isArray(colorCodes) ? colorCodes : [];
};

const defaultParsePayouts = (betCode: IServerTableConfigResponse['betCode']): PayoutType[] => {
  if (!betCode) {
    return [];
  } else {
    const bets = Object.keys(betCode);
    const payouts: PayoutType[] = [];
    bets.forEach((bet) => {
      if (betCode[bet] !== undefined) {
        payouts.push({
          code: parseInt(bet),
          payout: Number(betCode[bet]),
        });
      }
    });
    return payouts;
  }
};

export const mainTableConfigParser = ({
  resResult,
  parseBetLimits,
  parsePayouts,
}: {
  resResult: IServerTableConfigResponse;
  parseBetLimits: ParseBetLimitsType;
  parsePayouts?: ParsePayoutsType;
}): TableConfigType => {
  if (resResult.params?.errorCode?.toString() === '1052') {
    throw new ForbiddenError(ForbiddenErrors.TABLE_BLOCKED_FOR_COUNTRY);
  }
  if (
    !resResult.params ||
    !resResult.betCode ||
    !resResult.operatorGameId ||
    !resResult.tableId ||
    !resResult.tableName
  ) {
    throw new Error('Table config does not have required data');
  }
  try {
    return {
      aamsSessionId: resResult.params.aamsSessionId,
      aamsTicket: resResult.params.aamsTicket,
      betCode: resResult.betCode,
      betLimits: parseBetLimits(resResult.params),
      bettingTime: resResult.params.betting_time,
      chipAmounts: resResult.params.chip_amounts
        ? resResult.params.chip_amounts.split(',').map((el: string) => Number.parseFloat(el))
        : [],
      chipColorCodes: chipColorCodesNormalizer(resResult.params.chip_color_codes),
      dedicatedLanguage: resResult.params.dedicatedLanguage,
      defaultChipOrder: resResult.params.default_chip_order,
      enableVideoStats: resResult.params.enable_video_stats,
      gameStatsAddress: resResult.params.gamestats_address,
      gameTypeId: resResult.params.gametype_id,
      gsAddress: resResult.params.gs_address,
      hlsLocation: resResult.params.hls_location,
      hideLobby: resResult.params.hide_lobby,
      idleLimit: resResult.params.idle_limit_games,
      isPrizeDropBetDataSendEnabled: resResult.params.isPrizeDropBetDataSendEnabled,
      isPromoBetDataSendEnabled: resResult.params.isPromoBetDataSendEnabled,
      openHours: resResult.params.open_hours,
      openTime: resResult.params.openTime,
      operatorGameId: resResult.operatorGameId,
      operatorTheme: resResult.params.operator_theme,
      perPayoutBetMaxLimit: resResult.params.per_bet_payout_max,
      payouts: parsePayouts ? parsePayouts(resResult.params) : defaultParsePayouts(resResult.betCode),
      retrieveVideoQuality: resResult.params.retrive_video_quality,
      sessionLaunchTimePerTable: resResult.params.session_launch_time_per_table,
      storeVideoQuality: resResult.params.store_video_quality,
      subtitlesAvailable: resResult.params.subtitles_available === true,
      subtitle: resResult.params.subtitle?.split(',') ?? [],
      tableBetLimit: {
        max: resResult.params.table_bet_max_limit,
        min: resResult.params.table_bet_min_limit,
      },
      autoplayConfig: resResult.params.autoplay,
      tableClosed: resResult.params.table_closed ?? true,
      tableName: resResult.tableName,
      tableType: resResult.params.table_type,
      videoLocation: resResult.params.video_location,
      videoPlayerVersion: resResult.params.video_player_version,
      winMessageBranding: resResult.params.youwinfeature ?? WinMessageBranding.ONLY_PP_BRANDING,
      wsAddress: resResult.params.ws_address,
      broadcasterWsAddress: resResult.params.broadcaster_ws + '/broadcast',
      broadcasterEnabled: resResult.params.broadcaster_enabled,
      chatVisibleByDefault: resResult.params.chatVisibleByDefault,
      isInfiniteAutoPlayEnabled: resResult.params.isInfiniteAutoPlayEnabled,
      maxMultiplier: resResult.params.max_multiplier,
      payoutBetMaxLimit: resResult.params.payout_bet_max_limit,
      customMultiplierCap: resResult.params.custommultipliercap,
      tablePayoutMax: resResult.params.table_payout_max,
      canvasEnabled: resResult.params.canvasEnabled,
      euroTablePayoutMax: resResult.params.euro_table_payout_max,
      chatDisabled: resResult.params.chat_disabled,
      tableVariant: resResult.params.tableVariant,
      showPPBranding: resResult.params.show_pp_branding ?? true,
      hasNoCommissionToggle: resResult.params.hasnocommissiontoggle,
      audioMode: resResult.params.audioMode,
      megaBaccaratMegaFeePercent: resResult.params.megabaccarat_mega_fee_percent,
      disableChineseChar: resResult.params.disable_chinese_char,
      disableSideBets: resResult.params.disableSideBets,
      multiSeat: resResult.params.multiseat,
      useChroma: resResult.params.useChroma,
      themeHslaColorCode: resResult.params.themeHslaColorCode,
      operatorMusic: resResult.params.operator_music,
      bgMusic: resResult.params.bg_music,
      defaultBetAmount: resResult.params.default_bet_amount,
      jurisdiction: getJurisdictionData(resResult.params.jurisdiction_functionalities),
      multiSeatLimit: resResult.params.multiseat_limit,
      maxConcurrentPlayers: resResult.params.max_concurrent_players,
      sidebets: resResult.params.sidebets,
      betbehind: resResult.params.betbehind,
      tableCategory: resResult.params.table_category,
      perfectPairsMaxBet: resResult.params.perfectpairs_max_bet,
      perfectPairsMinBet: resResult.params.perfectpairs_min_bet,
      twentyOne3MaxBet: resResult.params.twentyone_3_max_bet,
      twentyOne3MinBbet: resResult.params.twentyone_3_min_bet,
      betBehindMaxBet: resResult.params.betbehind_max_bet,
      betBehindMinBet: resResult.params.betbehind_min_bet,
      disableBJVideoPanning: resResult.params.disableBJVideoPanning,
      dealNow: resResult.params.dealNow,
      dealNowMax: resResult.params.dealNowMax,
      minBalanceToPlay: resResult.params.min_balance_to_play,
    };
  } catch {
    throw new Error('Something is missing in table config');
  }
};
